import { resolveEnvironment, type AppEnvironment } from './environment'

/**
 * Per-stage configuration.
 *
 * Firebase web config is public by design — those values identify a project, they do not
 * authorize anything. The real gates are the Firebase Auth provider settings, the
 * deny-all Firestore rules, and the backend's domain check. Committing them avoids
 * Amplify env-var drift and makes local/staging parity trivial.
 *
 * NOTHING SECRET GOES IN A VITE_* VARIABLE — it ships to the browser.
 */

export type FirebaseClientConfig = {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

// TODO(rahul): fill these in from the Firebase console once auth-workboard-staging and
// auth-workboard-prod exist. Until then set VITE_FIREBASE_* in .env.local, which wins.
const firebaseStaging: FirebaseClientConfig = {
  apiKey: "AIzaSyDhUHMyWHiiEGLyR_vi7Qu23QAOYmr5Dq0",
  authDomain: "auth-workboard-staging.firebaseapp.com",
  projectId: "auth-workboard-staging",
  storageBucket: "auth-workboard-staging.firebasestorage.app",
  messagingSenderId: "922854289949",
  appId: "1:922854289949:web:ba6c905cffeec7dfdbccf4"
};

const firebaseProd: FirebaseClientConfig = {
  apiKey: "AIzaSyAMLs1W0A0WfeI_oVau5BnlWZWn4w8IXtc",
  authDomain: "auth-workboard-prod.firebaseapp.com",
  projectId: "auth-workboard-prod",
  storageBucket: "auth-workboard-prod.firebasestorage.app",
  messagingSenderId: "279025904283",
  appId: "1:279025904283:web:382f59db2d041baa5cb43e"
};

/**
 * API Gateway endpoints, per stage.
 *
 * `prod` was an empty string until the first production deploy existed. An empty baseURL is
 * not inert — axios resolves the request against the page's own origin, so every call went
 * to https://tapwork.taptalent.io/<path> and Amplify answered 404. If a stage here is ever
 * blank again, that is the symptom to expect.
 */
const API_BASE_URL: Record<AppEnvironment, string> = {
  local: 'http://localhost:4100/',
  staging: 'https://80p02btv9e.execute-api.ap-south-1.amazonaws.com/',
  prod: 'https://zhe6c9x172.execute-api.ap-south-1.amazonaws.com/',
}

/**
 * Canonical browser-facing Tapwork origins.
 *
 * Keep this separate from API_BASE_URL. Share/copy actions must never expose the API
 * Gateway hostname just because that is where data requests are sent.
 */
const APP_BASE_URL: Record<AppEnvironment, string> = {
  local: 'http://localhost:4200/',
  staging: 'https://staging-tapwork.taptalent.io/',
  prod: 'https://tapwork.taptalent.io/',
}

const environment = resolveEnvironment()

/** Env vars win over the checked-in defaults, so local overrides need no code change. */
function envOr(key: string, fallback: string): string {
  return (import.meta.env[key] as string | undefined) || fallback
}

const baseFirebase = environment === 'prod' ? firebaseProd : firebaseStaging

export const appConfig = {
  environment,
  isLocal: environment === 'local',
  apiBaseUrl: envOr('VITE_API_BASE_URL', API_BASE_URL[environment]),
  /** Canonical human-facing origin used for copied/deep links. */
  appBaseUrl: envOr('VITE_APP_URL', APP_BASE_URL[environment]),
  /** Google sign-in is hinted to this workspace; the backend enforces it for real. */
  allowedEmailDomain: envOr('VITE_ALLOWED_EMAIL_DOMAIN', 'taptalent.ai'),
  useEmulator: import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true',
  authEmulatorUrl: envOr('VITE_FIREBASE_AUTH_EMULATOR_URL', 'http://127.0.0.1:9099'),
  firebase: {
    apiKey: envOr('VITE_FIREBASE_API_KEY', baseFirebase.apiKey),
    authDomain: envOr('VITE_FIREBASE_AUTH_DOMAIN', baseFirebase.authDomain),
    projectId: envOr('VITE_FIREBASE_PROJECT_ID', baseFirebase.projectId),
    storageBucket: envOr('VITE_FIREBASE_STORAGE_BUCKET', baseFirebase.storageBucket),
    messagingSenderId: envOr('VITE_FIREBASE_MESSAGING_SENDER_ID', baseFirebase.messagingSenderId),
    appId: envOr('VITE_FIREBASE_APP_ID', baseFirebase.appId),
  } satisfies FirebaseClientConfig,
} as const
